import { useEffect, useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'
import type { Clinic, Provider } from '../types'
import { isValidPhone, isValidEmail } from '../utils/validators'
import {
  toggleClinicAssignment, toggleDefaultClinic, submittedAssignments, unlistedAssignedCount as countUnlisted,
} from '../utils/providerAssignments'
import PhoneInput from './PhoneInput'
import EmailInput from './EmailInput'

interface DoctorsListProps {
  providers: Provider[]
  clinics: Clinic[]
  clinicId: string
  isAdmin: boolean
  onAddProvider: (provider: Omit<Provider, 'id'>) => Promise<void>
  onUpdateProvider: (
    id: string,
    provider: Partial<Omit<Provider, 'id'>> & Pick<Provider, 'clinicIds' | 'defaultForClinicIds'>,
  ) => Promise<void>
  onDeleteProvider: (id: string) => Promise<void>
}

interface ProviderForm {
  firstName: string
  lastName: string
  npi: string
  taxId: string
  phone: string
  email: string
  clinicIds: string[]
  defaultForClinicIds: string[]
}

const emptyForm: ProviderForm = {
  firstName: '', lastName: '', npi: '', taxId: '', phone: '', email: '',
  clinicIds: [], defaultForClinicIds: [],
}

// Validation helpers
const isValidNpi = (v: string) => /^\d{10}$/.test(v)
const isValidTaxId = (v: string) => v === '' || /^\d{9}$/.test(v)

/** Max clinic chips shown inline per row; the rest collapse into "+N more". */
const MAX_CHIPS = 2

type SortCol = 'lastName' | 'firstName' | 'npi' | 'email'
type SortDir = 'asc' | 'desc'

function ColTh({
  label, col, sortBy, sortDir, onSort, disabled,
}: {
  label: string; col: SortCol; sortBy: SortCol; sortDir: SortDir; onSort: (c: SortCol) => void; disabled?: boolean
}) {
  const active = !disabled && sortBy === col
  return (
    <th
      className={`clinics-table__th${!disabled ? ' sortable-th' : ''}${active ? ' sortable-th--active' : ''}`}
      onClick={disabled ? undefined : () => onSort(col)}
    >
      {label}
      {active && <span className="sort-arrow">{sortDir === 'asc' ? '↑' : '↓'}</span>}
    </th>
  )
}

/** Clinic assignment chips: up to MAX_CHIPS named chips, rest in "+N more" with a hover tooltip. */
function ClinicChips({ clinicIds, clinicsById }: { clinicIds: string[]; clinicsById: Map<string, Clinic> }) {
  if (clinicIds.length === 0) return <span className="clinic-chips__none">—</span>
  const named = clinicIds.filter(id => clinicsById.has(id))
  const unknownCount = clinicIds.length - named.length
  const visible = named.slice(0, MAX_CHIPS)
  const overflow = clinicIds.length - visible.length
  const hiddenNames = named.slice(MAX_CHIPS).map(id => clinicsById.get(id)!.name)
  const tooltip = [
    ...hiddenNames,
    ...(unknownCount > 0 ? [`${unknownCount} other clinic${unknownCount === 1 ? '' : 's'}`] : []),
  ].join(' · ')
  return (
    <div className="clinic-chips">
      {visible.map(id => (
        <span key={id} className="clinic-chip">{clinicsById.get(id)!.name}</span>
      ))}
      {overflow > 0 && (
        <span className="clinic-chip clinic-chip--more">
          +{overflow} more
          <span className="clinic-chip__tip">{tooltip}</span>
        </span>
      )}
    </div>
  )
}

export default function DoctorsList({ providers, clinics, clinicId, isAdmin, onAddProvider, onUpdateProvider, onDeleteProvider }: DoctorsListProps) {
  const [doctorSearch, setDoctorSearch] = useState('')
  const [filterClinicId, setFilterClinicId] = useState<string>('all')
  const [sortBy, setSortBy] = useState<SortCol>('lastName')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [form, setForm] = useState<ProviderForm>(emptyForm)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showSlideout, setShowSlideout] = useState(false)
  const [attempted, setAttempted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const clinicsById = useMemo(() => new Map(clinics.map(c => [c.id, c])), [clinics])
  const editing = editingId ? providers.find(p => p.id === editingId) ?? null : null

  // Identity is org-shared once a provider spans clinics: admins always edit;
  // a manager may edit only while the provider exists solely at their clinic.
  const solelyMine = !editing || (editing.clinicIds.length === 1 && editing.clinicIds[0] === clinicId)
  const canEditIdentity = isAdmin || solelyMine
  // Deleting removes the org-wide record, so it tracks the same gate as
  // identity today; named separately because the rules express it separately.
  const canDelete = isAdmin || solelyMine

  // Duplicate-NPI guard (#98): the same NPI must not exist on two org
  // records. In create mode we offer to assign the existing provider
  // instead; in edit mode (retyping an NPI onto another provider's) we
  // just block the save.
  const npiDuplicate = isValidNpi(form.npi)
    ? providers.find(p => p.npi === form.npi && p.id !== editingId) ?? null
    : null

  function handleSort(col: SortCol) {
    if (col === sortBy) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortBy(col); setSortDir('asc') }
  }

  function openCreate() {
    // Pre-assign the clinic the admin is filtering by; otherwise the caller's
    // default. Non-admins are always locked to their own clinic.
    const target = isAdmin && filterClinicId !== 'all' ? filterClinicId : clinicId
    setForm({ ...emptyForm, clinicIds: target ? [target] : [] })
    setAttempted(false)
    setEditingId(null)
    setSubmitError(null)
    setShowSlideout(true)
  }

  function openEdit(provider: Provider) {
    setForm({
      firstName: provider.firstName, lastName: provider.lastName,
      npi: provider.npi, taxId: provider.taxId ?? '',
      phone: provider.phone, email: provider.email,
      clinicIds: provider.clinicIds,
      defaultForClinicIds: provider.defaultForClinicIds,
    })
    setAttempted(false)
    setEditingId(provider.id)
    setSubmitError(null)
    setShowSlideout(true)
  }

  function closeSlideout() {
    setForm(emptyForm)
    setAttempted(false)
    setEditingId(null)
    setShowSlideout(false)
    setSubmitError(null)
  }

  useEffect(() => {
    if (!showSlideout) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !saving) closeSlideout()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [showSlideout, saving])

  /** Switch the create slideout to editing the NPI-matched provider, carrying over every clinic selected in the create form. */
  function assignExistingProvider(provider: Provider) {
    const targets = form.clinicIds.filter(c => !provider.clinicIds.includes(c))
    openEdit(provider)
    if (targets.length > 0) {
      setForm(f => ({ ...f, clinicIds: [...f.clinicIds, ...targets] }))
    }
  }

  // Assignment edits go through the pure helpers (providerAssignments.ts) so
  // the #294 invariant — hidden/archived assignments survive every edit and
  // save untouched — is enforced and unit-tested in one place.
  function toggleClinic(id: string) {
    setForm(f => ({ ...f, ...toggleClinicAssignment(f, id) }))
  }

  function toggleDefault(id: string) {
    setForm(f => ({ ...f, ...toggleDefaultClinic(f, id) }))
  }

  function buildProvider(): Omit<Provider, 'id'> {
    return {
      firstName: form.firstName, lastName: form.lastName,
      npi: form.npi, taxId: form.taxId, phone: form.phone, email: form.email,
      ...submittedAssignments(form),
    }
  }

  /**
   * Update payload for an existing provider. When the caller can't edit
   * identity (manager on a shared provider), send ONLY the assignment
   * arrays: the rules restrict their update to those keys, and writing an
   * identity key the doc happens to lack (e.g. `taxId: ''` on a legacy doc
   * with no taxId) would register as an affected key and get the whole
   * save denied.
   */
  function buildUpdate(): Partial<Omit<Provider, 'id'>> & Pick<Provider, 'clinicIds' | 'defaultForClinicIds'> {
    // buildProvider() already ends in submittedAssignments(form), so the
    // admin branch needs no second copy of the assignment fields.
    return canEditIdentity ? buildProvider() : submittedAssignments(form)
  }

  async function handleSubmit() {
    setAttempted(true)
    setSubmitError(null)
    if (!canSubmit || npiDuplicate) return
    setSaving(true)
    try {
      if (editingId) {
        await onUpdateProvider(editingId, buildUpdate())
      } else {
        await onAddProvider(buildProvider())
      }
      closeSlideout()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      setSubmitError(`Failed to ${editingId ? 'save' : 'create'} provider. ${msg}`)
      Sentry.captureException(err, { tags: { action: editingId ? 'updateProvider' : 'addProvider' } })
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(id: string) {
    setSubmitError(null)
    setSaving(true)
    try {
      await onDeleteProvider(id)
      closeSlideout()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      setSubmitError(`Failed to delete provider. ${msg}`)
      Sentry.captureException(err, { tags: { action: 'deleteProvider' } })
    } finally {
      setSaving(false)
    }
  }

  /** Manager path for a shared provider: drop only my clinic's assignment, keep the org record. */
  async function handleRemoveFromMyClinic() {
    if (!editing) return
    setSubmitError(null)
    setSaving(true)
    try {
      await onUpdateProvider(editing.id, {
        clinicIds: editing.clinicIds.filter(c => c !== clinicId),
        defaultForClinicIds: editing.defaultForClinicIds.filter(c => c !== clinicId),
      })
      closeSlideout()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      setSubmitError(`Failed to remove provider from clinic. ${msg}`)
      Sentry.captureException(err, { tags: { action: 'removeProviderFromClinic' } })
    } finally {
      setSaving(false)
    }
  }

  function getFilteredAndSorted() {
    const q = doctorSearch.toLowerCase().trim()
    const base = filterClinicId === 'all'
      ? providers
      : providers.filter(p => p.clinicIds.includes(filterClinicId))
    const list = q
      ? base.filter(p =>
          p.firstName.toLowerCase().includes(q) ||
          p.lastName.toLowerCase().includes(q) ||
          p.email.toLowerCase().includes(q) ||
          p.npi.includes(q)
        )
      : [...base]
    const mult = sortDir === 'asc' ? 1 : -1
    list.sort((a, b) => {
      const va = (a[sortBy] ?? '').toString()
      const vb = (b[sortBy] ?? '').toString()
      return mult * va.localeCompare(vb, undefined, { sensitivity: 'base' })
    })
    return list
  }

  const errors = {
    firstName: form.firstName.trim().length === 0,
    lastName:  form.lastName.trim().length === 0,
    npi:       !isValidNpi(form.npi),
    taxId:     !isValidTaxId(form.taxId),
    phone:     !(form.phone === '' || isValidPhone(form.phone)),
    email:     !isValidEmail(form.email),
    clinicIds: form.clinicIds.length === 0,
  }
  const canSubmit = !Object.values(errors).some(Boolean)
  const filtered = getFilteredAndSorted()

  function inv(field: keyof typeof errors) {
    return attempted && errors[field] ? ' is-invalid' : ''
  }

  // Assignment rows come from the clinics the viewer can name (admins: all
  // active clinics; managers: just their own doc). Assignments beyond that set
  // are preserved on save regardless (providerAssignments.ts — the form only
  // edits ids it displays). For a MANAGER the count is worth a note: it
  // explains why their list is legitimately incomplete (other clinics, run by
  // their own teams). For an ADMIN the unlisted clinics are the archived ones
  // — a closed-out decision the admin can't name, reach, or clear from here —
  // so no note renders (#294); the assignments themselves still round-trip.
  // isAdmin short-circuits: the note is the count's only consumer and it never
  // renders for admins, so don't compute it for them on every keystroke.
  const unlistedAssignedCount = isAdmin ? 0 : countUnlisted(form.clinicIds, clinicsById)
  // The header counts what the panel can render. A manager's remainder is
  // explained by the note below; an admin's remainder is archived clinics the
  // panel deliberately doesn't surface (#294) — counting them would put a
  // "2 clinics" header over a single checkbox with nothing explaining the gap.
  const headerAssignedCount = isAdmin
    ? form.clinicIds.filter(id => clinicsById.has(id)).length
    : form.clinicIds.length
  const headerName = `${form.firstName.trim()} ${form.lastName.trim()}`.trim()
  const defaultAtNames = form.defaultForClinicIds
    .filter(id => clinicsById.has(id))
    .map(id => clinicsById.get(id)!.name)

  return (
    <>
      <div className="doctors-view">
        <div className="doctors-view__header">
          <div className="doctors-view__title-row">
            <svg className="doctors-view__icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4.8 2.6A2 2 0 1 0 3 6.66V9a6 6 0 0 0 12 0V6.66A2 2 0 1 0 13.2 2.6" />
              <path d="M9 15v3a3 3 0 0 0 3 3h3a3 3 0 0 0 3-3v-1a2 2 0 0 0-2-2h-1" />
              <circle cx="9" cy="11" r="1" fill="currentColor" stroke="none" />
            </svg>
            <h2 className="doctors-view__title">Providers</h2>
          </div>
          <div className="users-view__header-right">
            {isAdmin && (
              <select
                className="toolbar-select"
                value={filterClinicId}
                onChange={e => setFilterClinicId(e.target.value)}
                aria-label="Filter by clinic"
              >
                <option value="all">All Clinics</option>
                {clinics.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
            <input
              type="search"
              className="users-view__search"
              placeholder="Search by name, email, NPI..."
              value={doctorSearch}
              onChange={e => setDoctorSearch(e.target.value)}
            />
            <button className="create-doctor-btn create-doctor-btn--primary" onClick={openCreate}>
              + Add Provider
            </button>
          </div>
        </div>

        <div className="clinics-table-wrap">
          {filtered.length === 0 ? (
            <p className="clinics-view__empty">
              {doctorSearch ? 'No providers match your search.' : 'No providers yet. Create one to get started.'}
            </p>
          ) : (
            <table className="clinics-table">
              <colgroup>
                <col style={{ width: '13%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: '24%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '34%' }} />
              </colgroup>
              <thead>
                <tr className="clinics-table__head-row">
                  {/* First name reads before last name (#295) — matching the edit
                      form below and "Dr. {first} {last}" everywhere else. Sort
                      order is a separate decision: last-name ascending stays the
                      default, so the arrow simply lives on the second column. */}
                  <ColTh label="First Name" col="firstName" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} disabled={filtered.length <= 1} />
                  <ColTh label="Last Name" col="lastName" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} disabled={filtered.length <= 1} />
                  <ColTh label="Email" col="email" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} disabled={filtered.length <= 1} />
                  <ColTh label="NPI" col="npi" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} disabled={filtered.length <= 1} />
                  <th className="clinics-table__th">Clinics</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr
                    key={p.id}
                    className={`clinics-table__row${editingId === p.id && showSlideout ? ' clinics-table__row--selected' : ''}`}
                    onClick={() => openEdit(p)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEdit(p) } }}
                  >
                    <td className="clinics-table__cell clinics-table__cell--name">{p.firstName}</td>
                    <td className="clinics-table__cell">{p.lastName}</td>
                    <td className="clinics-table__cell clinics-table__cell--email" title={p.email}>{p.email}</td>
                    <td className="clinics-table__cell">{p.npi}</td>
                    <td className="clinics-table__cell">
                      <ClinicChips clinicIds={p.clinicIds} clinicsById={clinicsById} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showSlideout && (
        <>
          <div className="provider-slideout-scrim" onClick={closeSlideout} />
          <aside className="provider-slideout" role="dialog" aria-label={editingId ? 'Edit provider' : 'Add provider'}>
            <div className="provider-slideout__head">
              <div>
                <div className="provider-slideout__title">
                  {editingId ? (headerName ? `Dr. ${headerName}` : 'Edit Provider') : 'Add Provider'}
                  {editingId && isValidNpi(form.npi) && <span className="provider-slideout__npi">NPI {form.npi}</span>}
                </div>
                <div className="provider-slideout__sub">
                  {headerAssignedCount > 0
                    ? `${headerAssignedCount} clinic${headerAssignedCount === 1 ? '' : 's'}${defaultAtNames.length > 0 ? ` · Default at ${defaultAtNames.join(', ')}` : ''}`
                    : form.clinicIds.length > 0
                      // Admin, archived-only assignments: not "Not assigned" —
                      // the stored assignments exist and survive a save.
                      ? 'No active-clinic assignments'
                      : 'Not assigned to a clinic yet'}
                </div>
              </div>
              <button className="doctor-modal__close" onClick={closeSlideout} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="provider-slideout__body">
              <div className="provider-slideout__sect">
                <div className="provider-slideout__sect-head">
                  <span className="provider-slideout__sect-title">Identity</span>
                  {!canEditIdentity && (
                    <span className="provider-slideout__lock">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                      Shared across clinics — only admins can edit identity
                    </span>
                  )}
                </div>
                <div className="add-doctor-form__fields provider-slideout__fields">
                  <div className="form-field">
                    <label className="form-field__label">First Name *</label>
                    <input className={`form-field__input${inv('firstName')}`} value={form.firstName} disabled={!canEditIdentity} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} placeholder="Sarah" />
                  </div>
                  <div className="form-field">
                    <label className="form-field__label">Last Name *</label>
                    <input className={`form-field__input${inv('lastName')}`} value={form.lastName} disabled={!canEditIdentity} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} placeholder="Chen" />
                  </div>
                  <div className="form-field">
                    <label className="form-field__label">NPI *</label>
                    <input className={`form-field__input${inv('npi')}`} value={form.npi} disabled={!canEditIdentity} onChange={e => setForm(f => ({ ...f, npi: e.target.value.replace(/\D/g, '').slice(0, 10) }))} placeholder="1234567890" inputMode="numeric" maxLength={10} />
                  </div>
                  <div className="form-field">
                    <label className="form-field__label">Tax ID</label>
                    <input className={`form-field__input${inv('taxId')}`} value={form.taxId} disabled={!canEditIdentity} onChange={e => setForm(f => ({ ...f, taxId: e.target.value.replace(/\D/g, '').slice(0, 9) }))} placeholder="123456789" inputMode="numeric" maxLength={9} />
                  </div>
                  <div className="form-field form-field--full">
                    <label className="form-field__label">Email *</label>
                    <EmailInput className={`form-field__input${inv('email')}`} value={form.email} disabled={!canEditIdentity} onValueChange={val => setForm(f => ({ ...f, email: val }))} placeholder="dr.chen@lakewoodfm.com" />
                  </div>
                  <div className="form-field">
                    <label className="form-field__label">Phone</label>
                    <PhoneInput className={`form-field__input${inv('phone')}`} value={form.phone} disabled={!canEditIdentity} onValueChange={val => setForm(f => ({ ...f, phone: val }))} placeholder="(555) 555-5555" />
                  </div>
                </div>
                {npiDuplicate && (
                  <div className="provider-dup-banner">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9v4M12 17h.01" /></svg>
                    <span className="provider-dup-banner__text">
                      <b>Dr. {npiDuplicate.firstName} {npiDuplicate.lastName} already exists</b> with this NPI.
                      Assign them to another clinic instead of creating a duplicate.
                    </span>
                    <button className="provider-dup-banner__btn" onClick={() => assignExistingProvider(npiDuplicate)}>
                      Use existing provider
                    </button>
                  </div>
                )}
              </div>

              <div className="provider-slideout__sect">
                <div className="provider-slideout__sect-head">
                  <span className="provider-slideout__sect-title">Clinic assignments</span>
                </div>
                <div className={`provider-assign-box${attempted && errors.clinicIds ? ' is-invalid' : ''}`}>
                  {clinics.map(c => {
                    const assigned = form.clinicIds.includes(c.id)
                    const isDefaultHere = form.defaultForClinicIds.includes(c.id)
                    return (
                      <div key={c.id} className={`provider-assign-row${assigned ? ' provider-assign-row--on' : ''}`}>
                        <label className="provider-assign-row__main">
                          <input
                            type="checkbox"
                            checked={assigned}
                            onChange={() => toggleClinic(c.id)}
                          />
                          <span className="provider-assign-row__name">{c.name}</span>
                        </label>
                        {assigned && (
                          <button
                            type="button"
                            className={`provider-default-pill${isDefaultHere ? ' provider-default-pill--on' : ''}`}
                            onClick={() => toggleDefault(c.id)}
                          >
                            {isDefaultHere ? '★ Default' : 'Set default'}
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
                {unlistedAssignedCount > 0 && !isAdmin && (
                  <div className="provider-assign-note">
                    Also assigned at {unlistedAssignedCount} clinic{unlistedAssignedCount === 1 ? '' : 's'} not shown here
                    {' — managed by their own teams or an admin'}.
                  </div>
                )}
                <span className="form-field__hint">Default = used for eligibility checks at that clinic when no specific provider is assigned</span>
              </div>

              {submitError && <p className="clinic-save-error">{submitError}</p>}
            </div>

            <div className="provider-slideout__foot">
              {editingId ? (
                canDelete ? (
                  <button className="add-doctor-form__delete" onClick={() => handleDelete(editingId)} disabled={saving}>
                    Delete provider
                  </button>
                ) : (
                  <button className="add-doctor-form__delete" onClick={handleRemoveFromMyClinic} disabled={saving || !editing?.clinicIds.includes(clinicId)}>
                    Remove from my clinic
                  </button>
                )
              ) : (
                <span />
              )}
              <div className="add-doctor-form__actions-right">
                <button className="add-doctor-form__cancel" onClick={closeSlideout}>Cancel</button>
                <button className="check-btn add-doctor-form__submit" onClick={handleSubmit} disabled={saving || !!npiDuplicate}>
                  {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Provider'}
                </button>
              </div>
            </div>
          </aside>
        </>
      )}
    </>
  )
}
